// ============================================================
// Conversation — le fil et la saisie, partagés par la page Assistant et
// par le panneau du devis.
// ------------------------------------------------------------
// Une conversation naît au PREMIER message, pas à l'ouverture du panneau :
// ouvrir pour regarder ne crée rien en base. Sous chaque réponse, les
// outils que l'assistant a consultés et ce qu'elle a coûté — un chat qui
// consomme en continu doit se voir consommer (§2bis, « Coût »).
// ============================================================

import { useEffect, useRef, useState } from 'react';
import Button from '../../ui/Button.jsx';
import { Textarea } from '../../ui/TextField.jsx';
import { ErrorState } from '../../ui/States.jsx';
import api, { READ_TIMEOUT_MS } from '../../lib/api.js';
import MessageAssistant from './MessageAssistant.jsx';
import CarteProposition from './CarteProposition.jsx';
import './assistant.css';
import { formatNumber } from '../../lib/format.js';

// Le coût d'une réponse se compte en millièmes d'euro : `formatEuro` l'arrondirait à 0,00 €.
const euros = (v) => (v == null ? '' : formatNumber(v, '€'));

export function Conversation({ conversationId, devisId = null, pieceId = null, onCree, exemples = [] }) {
  const [messages, setMessages] = useState([]);
  const [propositions, setPropositions] = useState([]);
  const [question, setQuestion] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const fin = useRef(null);

  useEffect(() => {
    let vivant = true;
    if (!conversationId) {
      setMessages([]);
      setPropositions([]);
      return undefined;
    }
    api.get(`/assistant/conversations/${conversationId}`)
      .then((r) => {
        if (!vivant) return;
        setMessages(r.messages ?? []);
        setPropositions(r.propositions ?? []);
      })
      .catch((err) => { if (vivant) setErreur(err); });
    return () => { vivant = false; };
  }, [conversationId]);

  useEffect(() => { fin.current?.scrollIntoView?.({ block: 'end' }); }, [messages, envoi]);

  async function envoyer(texte = question) {
    const q = texte.trim();
    if (!q || envoi) return;
    setEnvoi(true);
    setErreur(null);
    setMessages((m) => [...m, { id: `local-${Date.now()}`, role: 'user', contenu: q }]);
    setQuestion('');
    try {
      let id = conversationId;
      if (!id) {
        const { conversation } = await api.post('/assistant/conversations', { devis_id: devisId });
        id = conversation.id;
        onCree?.(id);
      }
      const r = await api.post(
        `/assistant/conversations/${id}/messages`,
        { question: q, piece_id: pieceId },
        { timeout: READ_TIMEOUT_MS },
      );
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith('local-')), r.question, r.reponse]);
      // Une réponse peut avoir déposé une proposition : on relit les cartes.
      const relu = await api.get(`/assistant/conversations/${id}`);
      setPropositions(relu.propositions ?? []);
    } catch (err) {
      setErreur(err);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="assistant-fil">
      <div className="assistant-fil__messages" aria-live="polite">
        {messages.length === 0 && !envoi ? (
          <div className="field__hint">
            <p>Posez une question sur un devis, une pièce, un prix, l’historique ou l’atelier.</p>
            {exemples.length ? (
              <ul className="assistant-fil__exemples">
                {exemples.map((e) => (
                  <li key={e}>
                    <Button size="sm" variant="ghost" onClick={() => envoyer(e)}>{e}</Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {messages.map((m) => (
          <div key={m.id} className={`assistant-message assistant-message--${m.role}`}>
            {m.role === 'assistant' ? <MessageAssistant texte={m.contenu} /> : <p>{m.contenu}</p>}
            {m.role === 'assistant' && (m.outils_utilises?.length || m.cout_eur != null) ? (
              <p className="assistant-message__trace field__hint">
                {m.outils_utilises?.length
                  ? `Consulté : ${[...new Set(m.outils_utilises.map((o) => o.nom))].join(', ')}`
                  : 'Aucun outil consulté'}
                {m.cout_eur != null ? ` · ${euros(m.cout_eur)}` : ''}
              </p>
            ) : null}
          </div>
        ))}
        {/* Les propositions, dans l'ordre où elles ont été faites. */}
        {[...propositions].sort((a, b) => a.id - b.id).map((p) => (
          <CarteProposition
            key={p.id}
            proposition={p}
            onChange={async () => {
              const relu = await api.get(`/assistant/conversations/${conversationId}`);
              setPropositions(relu.propositions ?? []);
            }}
            onModifier={(x) => setQuestion(`Modifie la proposition n° ${x.id} : `)}
          />
        ))}
        {envoi ? <p className="field__hint" role="status">L’assistant cherche dans la base…</p> : null}
        <div ref={fin} />
      </div>
      {erreur ? <ErrorState error={erreur} title="Pas de réponse" onRetry={() => setErreur(null)} /> : null}
      <form className="assistant-fil__saisie" onSubmit={(e) => { e.preventDefault(); envoyer(); }}>
        <Textarea
          label="Votre question"
          hideLabel
          rows={3}
          maxLength={4000}
          value={question}
          disabled={envoi}
          placeholder="Ex. : pourquoi cette pièce est à ce prix ?"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              envoyer();
            }
          }}
        />
        <Button type="submit" variant="primary" disabled={envoi || !question.trim()}>Envoyer</Button>
      </form>
    </div>
  );
}

export default Conversation;
